import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import bind from "memoize-bind";

import { st, SubBar, MainView, SidebarView, Button, PaginationNavigation, Message, Menu, FormControl } from "lib/index";
import {
  stateSelector,
  initializeView,
  destroyView,
  setParamsWithLocationQuery,
  changeFilters,
  changeFiltersAndResetOffset,
  changePagination,
  fetchRecords,
  selectOrders,
} from "./index";
import OrdersListFilters from "modules/orders/OrdersListView/OrdersListFilters";
import { createOrdersAgentDisbursement, createOrdersPlatformDisbursement } from "modules/orders/utils";
import FinancesListTable from "./FinancesListTable";

@connect(
  (state) => ({
    currentCompany: state.auth.company.model,
    currentProducts: state.auth.products.model || [],
    sidebarVisible: state.app.sidebarVisible,
    hasBulkActions: stateSelector(state).hasBulkActions,
    filters: stateSelector(state).filters,
    offset: stateSelector(state).offset,
    limit: stateSelector(state).limit,
    records: stateSelector(state).records.model && stateSelector(state).records.model.records,
    totalCount: stateSelector(state).records.model && stateSelector(state).records.model.meta.total_count,
    aggregations: stateSelector(state).records.model && stateSelector(state).records.model.aggregations,
    fetchDone: stateSelector(state).records.fetchDone,
    fetchLoading: stateSelector(state).records.fetchLoading,
    fetchFailed: stateSelector(state).records.fetchFailed,
    selectedOrdersIds: stateSelector(state).selectedOrdersIds,
    bulkActionLoading: state.orders.utils.callBulkOrdersAction.fetchLoading,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        initializeView,
        destroyView,
        setParamsWithLocationQuery,
        changeFilters,
        changeFiltersAndResetOffset,
        changePagination,
        fetchRecords,
        selectOrders,
        createOrdersAgentDisbursement,
        createOrdersPlatformDisbursement,
      },
      dispatch
    )
)
export default class FinancesListView extends React.PureComponent {
  componentDidMount() {
    this.props.initializeView();
    this.props.setParamsWithLocationQuery({
      company_id:
        !this.props.location.query.company_id &&
        !this.props.location.query.agent_id &&
        this.props.currentCompany.supplier
          ? this.props.currentCompany.id
          : undefined,
      agent_id:
        !this.props.location.query.company_id &&
        !this.props.location.query.agent_id &&
        !this.props.currentCompany.supplier &&
        this.props.currentCompany.agent
          ? this.props.currentCompany.id
          : undefined,
      ...this.props.location.query,
    });
  }

  componentWillUnmount() {
    this.props.destroyView();
  }

  handleViewModeChange(viewMode) {
    const { currentCompany, filters, changeFilters, changeFiltersAndResetOffset } = this.props;

    if (viewMode === "supplier") {
      changeFiltersAndResetOffset({
        ...filters,
        company_id: currentCompany.id,
        agent_id: filters.agent_id === currentCompany.id ? null : filters.agent_id,
      });
    } else {
      changeFiltersAndResetOffset({
        ...filters,
        company_id: filters.company_id === currentCompany.id ? null : filters.company_id,
        agent_id: currentCompany.id,
      });
    }
  }

  render() {
    const {
      currentCompany,
      currentProducts,
      hasBulkActions,
      fetchDone,
      fetchLoading,
      fetchFailed,
      bulkActionLoading,
      records,
      aggregations,
      filters,
      offset,
      limit,
      totalCount,
      selectedOrdersIds,
      sidebarVisible,
      fetchRecords,
      changeFilters,
      changePagination,
      selectOrders,
      createOrdersAgentDisbursement,
      createOrdersPlatformDisbursement,
      changeFiltersAndResetOffset,
    } = this.props;

    const currentViewMode =
      (filters.agent_id === currentCompany.id && "agent") ||
      (filters.company_id === currentCompany.id && "supplier") ||
      (currentCompany.supplier && "supplier") ||
      (currentCompany.agent && "agent") ||
      "supplier";
    const viewModeChangePossible = currentCompany.agent && currentCompany.supplier;
    const viewModeOptions = [
      ["supplier", I18n.humanAttributeName("order", "supplier")],
      ["agent", I18n.humanAttributeName("order", "agent")],
    ];

    return (
      <SidebarView.Container>
        <SidebarView.Sidebar label={I18n.t("papp.common.filters_title")} extended={sidebarVisible}>
          <OrdersListFilters
            currentCompany={currentCompany}
            currentProducts={currentProducts}
            aggregations={aggregations}
            filters={filters}
            onFiltersChange={(filters) => changeFiltersAndResetOffset(filters)}
          />
        </SidebarView.Sidebar>

        <SidebarView.Content hasSidebar={sidebarVisible}>
          <MainView.Container>
            <SubBar hasSidebar={sidebarVisible}>
              <div className="pull-left u-inlineFlexRow">
                <h1>{I18n.t("papp.finances.FinancesListView.title")}</h1>
              </div>

              <div className="pull-right">
                <div className="u-inlineFlexRow">
                  {hasBulkActions && (
                    <span className="u-marginRight">
                      {I18n.t("base.x_selected_records", {
                        count: selectedOrdersIds.length,
                      })}
                    </span>
                  )}

                  {selectedOrdersIds.length > 0 && st.authorize("create_agent_disbursement") && (
                    <Button
                      primary
                      as="a"
                      className="u-marginRight"
                      onClick={() => createOrdersAgentDisbursement(selectedOrdersIds)}
                      content={I18n.t("papp.finances.FinancesListView.create_agent_disbursement")}
                      disabled={bulkActionLoading}
                    />
                  )}

                  {selectedOrdersIds.length > 0 && st.authorize("create_platform_disbursement") && (
                    <Button
                      primary
                      as="a"
                      className="u-marginRight"
                      onClick={() => createOrdersPlatformDisbursement(selectedOrdersIds)}
                      content={I18n.t("papp.finances.FinancesListView.create_platform_disbursement")}
                      disabled={bulkActionLoading}
                    />
                  )}

                  {viewModeChangePossible && (
                    <Menu secondary pointing className="u-marginHorizontal u-marginVerticalNone">
                      {viewModeOptions.map(([viewMode, label]) => (
                        <Menu.Item
                          key={viewMode}
                          active={viewMode === currentViewMode}
                          onClick={() => this.handleViewModeChange(viewMode)}
                          content={label}
                        />
                      ))}
                    </Menu>
                  )}

                  <FormControl.Select
                    value={limit}
                    onChange={(limit) => changePagination({ offset, limit })}
                    options={[
                      [50, I18n.t("papp.common.x_per_page", { count: 50 })],
                      [100, I18n.t("papp.common.x_per_page", { count: 100 })],
                      [200, I18n.t("papp.common.x_per_page", { count: 200 })],
                      [500, I18n.t("papp.common.x_per_page", { count: 500 })],
                    ]}
                    className="u-mediumWidth u-marginHorizontal c-inputStandard"
                  />

                  {fetchDone && totalCount > 0 && (
                    <PaginationNavigation
                      offset={offset}
                      limit={limit}
                      totalCount={totalCount}
                      onChange={changePagination}
                      hideDefaultPerPage={true}
                    />
                  )}
                </div>
              </div>
            </SubBar>

            <MainView.Content grayBackground hasSubBar>
              <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={fetchRecords} />
              {fetchDone && totalCount === 0 && (
                <MainView.DimmerContent
                  icon="sentiment_neutral"
                  title={I18n.t("papp.finances.FinancesListView.no_records_title")}
                >
                  <p>{I18n.t("papp.finances.FinancesListView.no_records_message")}</p>
                </MainView.DimmerContent>
              )}
              {fetchDone && totalCount > 0 && (
                <FinancesListTable
                  orders={records}
                  selectedOrdersIds={selectedOrdersIds}
                  viewMode={st.authorize.company.is_st && currentViewMode === "agent" ? "st_agent" : currentViewMode}
                  start={offset + 1}
                  end={Math.min(offset + limit, totalCount)}
                  onSelectOrders={selectOrders}
                  selectable={hasBulkActions}
                />
              )}
            </MainView.Content>
          </MainView.Container>
        </SidebarView.Content>
      </SidebarView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/finances/FinancesListView/FinancesListView.jsx